'use client'

import React from 'react'
import { CircleAlert, Check, LogOut, PenLine, Plus, User } from 'lucide-react'
import event from '@/utils/event'
import Wallet from '@/utils/wallet'
import { cn } from '@/lib/utils'
import { bip322Capable } from '../lib/twetch-auth'
import { FEED_REFRESH_EVENT } from '../lib/use-twetch-feed'
import { useTwetch } from '../lib/twetch-store'
import { openProfileEditor } from '../lib/twetch-ui'
import { resolveTwetchImage } from '../lib/twetch-media'
import useViewerProfile from '../lib/use-viewer-profile'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import { useAlkanes } from '../lib/store'
import useSponsoredPost from '../lib/use-sponsored-post'
import TwetchTurnstile from './twetch-turnstile'
import type { TwetchSmartTag } from '../lib/twetch-api'
import useMarketSearch, { type MarketOption } from '../lib/use-market-search'

/// The post a composer answers: id for the API row, txid for the signed AIP
/// domain, author for the prompt.
export interface ReplyTarget {
	postId: number
	txid: string | null
	author: string
}

/// What a thesis box is about: an alkane by default, or any smart-tagged
/// market (`namespace: 'ordinalswallet'` for OW collections, runes, BRC-20, TAP).
export interface ComposerContext {
	id: string
	symbol: string
	namespace?: string
	/// The market's display name for prompts; the symbol is used when absent.
	label?: string
}

interface TwetchDoorProps {
	onPosted?: () => void
	context?: ComposerContext
	compact?: boolean
	replyTo?: ReplyTarget
	/// Put the cursor in the box on mount (a reply control was used).
	autoFocus?: boolean
	/// No card of its own: the host panel supplies the surface and padding.
	/// For sidebars, where a card inside a panel is one inset too many.
	bare?: boolean
	/// A control that chooses this composer's market, for a feed that isn't on
	/// one already. Writing a `$cashtag` tags the post just as well; one of the
	/// two has to have happened before there is anything to publish.
	marketPicker?: React.ReactNode
}

function withTickerPrefix(symbol: string, text: string): string {
	const ticker = `$${symbol.toUpperCase()}`
	return text.split(/\s+/).some((part) => part.toUpperCase() === ticker)
		? text
		: `${ticker} ${text}`.trim()
}

export default function TwetchDoor({
	onPosted,
	context,
	compact = false,
	replyTo,
	autoFocus = false,
	bare = false,
	marketPicker
}: TwetchDoorProps) {
	// The SITE's wallet session, not the terminal store's copy of it: the
	// composer lives on any page, and only the terminal feeds that store.
	const walletConnected = !!useLoggedIn()
	const watchAddress = useAlkanes((s) => s.watchAddress)
	const loggedIn = walletConnected || watchAddress !== null

	return (
		<Composer
			loggedIn={loggedIn}
			watchMode={watchAddress !== null}
			onPosted={onPosted}
			context={context}
			compact={compact}
			replyTo={replyTo}
			autoFocus={autoFocus}
			bare={bare}
			marketPicker={marketPicker}
		/>
	)
}

function Composer({
	loggedIn,
	watchMode,
	onPosted,
	context,
	compact,
	replyTo,
	autoFocus,
	bare,
	marketPicker
}: {
	loggedIn: boolean
	watchMode: boolean
	onPosted?: () => void
	context?: ComposerContext
	compact: boolean
	replyTo?: ReplyTarget
	autoFocus: boolean
	bare: boolean
	marketPicker?: React.ReactNode
}) {
	const signOut = useTwetch((s) => s.signOut)
	const viewer = useViewerProfile()
	const assets = useAlkanes((s) => s.assets)
	const address = Wallet.getTaprootAddress()
	const capable = bip322Capable(address)
	const [text, setText] = React.useState('')
	const [smartTags, setSmartTags] = React.useState<TwetchSmartTag[]>([])
	const [tokenActive, setTokenActive] = React.useState(0)
	const [tokenPickerDismissed, setTokenPickerDismissed] = React.useState(false)
	const [turnstileToken, setTurnstileToken] = React.useState<string | null>(null)
	const [turnstileFailed, setTurnstileFailed] = React.useState(false)
	const [turnstileResetKey, setTurnstileResetKey] = React.useState(0)
	const inputId = React.useId()
	const listboxId = React.useId()
	const inputRef = React.useRef<HTMLTextAreaElement | null>(null)

	React.useEffect(() => {
		if (autoFocus) inputRef.current?.focus({ preventScroll: true })
	}, [autoFocus])

	// The box grows with the thesis instead of scrolling three lines — capped
	// so a long post scrolls INSIDE a still-reasonable box, never the page.
	React.useLayoutEffect(() => {
		const el = inputRef.current
		if (!el) {
			return
		}
		el.style.height = 'auto'
		el.style.height = `${Math.min(el.scrollHeight, 200)}px`
	}, [text])
	const activeCashtag = React.useMemo(() => /(^|\s)\$([a-z0-9]{0,15})$/i.exec(text), [text])
	// The cashtag being written, or null when none is — what the market search
	// answers. Alkanes alone used to answer it, and only in the terminal, where
	// the store happens to hold them; every OW market answers now, anywhere.
	const { options: tokenOptions, loading: tokenLoading } = useMarketSearch(
		activeCashtag ? activeCashtag[2] : null
	)

	const composer = useSponsoredPost(() => {
		setText('')
		setSmartTags([])
		// Every mounted feed refetches — the thesis tab must show the post
		// that was just made from the trade box, not wait out a poll.
		event.emit(FEED_REFRESH_EVENT)
		onPosted?.()
	})
	const identity = composer.identity

	const changeText = (next: string) => {
		setText(next)
		setTokenActive(0)
		setTokenPickerDismissed(false)
		setSmartTags((current) =>
			current.filter((tag) =>
				new RegExp(`(^|[^A-Za-z0-9])\\$${tag.symbol}(?![A-Za-z0-9])`, 'i').test(next)
			)
		)
	}

	const selectToken = (option: MarketOption) => {
		if (!activeCashtag) return
		const start = activeCashtag.index + activeCashtag[1].length
		setText(`${text.slice(0, start)}$${option.symbol} `)
		setSmartTags((current) =>
			[
				...current.filter(
					(tag) => !(tag.namespace === option.namespace && tag.id === option.id)
				),
				{ namespace: option.namespace, id: option.id, symbol: option.symbol }
			].slice(-8)
		)
		setTokenPickerDismissed(false)
	}

	// A rejected first post has spent its single-use token (or never had one):
	// re-mount the widget so the retry carries a fresh token.
	const verificationRejected = composer.error?.kind === 'verification'
	const [rejectionHandled, setRejectionHandled] = React.useState(false)
	if (verificationRejected !== rejectionHandled) {
		setRejectionHandled(verificationRejected)
		if (verificationRejected) {
			setTurnstileToken(null)
			setTurnstileFailed(false)
			setTurnstileResetKey((key) => key + 1)
		}
	}

	// Every tag the post will actually carry — what `submitPost` sends, shown
	// as chips so nothing is attached silently.
	const effectiveTags = React.useMemo(() => {
		if (!context) {
			return smartTags
		}
		const contextualTag = {
			namespace: context.namespace ?? 'alkanes',
			id: context.id,
			symbol: context.symbol.toUpperCase()
		}
		return [
			...smartTags.filter(
				(tag) => !(tag.namespace === contextualTag.namespace && tag.id === contextualTag.id)
			),
			contextualTag
		].slice(-8)
	}, [smartTags, context])

	const busy = composer.phase === 'signing' || composer.phase === 'publishing'
	const verificationReady = identity !== null || turnstileToken !== null || turnstileFailed
	const canPost =
		loggedIn &&
		!watchMode &&
		composer.eligible === true &&
		capable &&
		verificationReady &&
		text.trim().length > 0 &&
		// A post here has to name a market — picked, or written as a cashtag.
		// Untagged, it would publish into a feed that could never show it.
		(!marketPicker || effectiveTags.length > 0) &&
		!busy
	const unavailable = watchMode || (loggedIn && (composer.eligible === false || !capable))
	const failed = composer.error !== null
	const posted = composer.phase === 'posted'
	const actionLabel = posted
		? 'Posted'
		: failed
		? 'Try again'
		: composer.phase === 'signing'
		? 'Check wallet'
		: composer.phase === 'publishing'
		? 'Posting…'
		: !loggedIn
		? 'Connect to post'
		: unavailable
		? 'Unavailable'
		: replyTo
		? 'Reply'
		: 'Post'
	const composerTestId = context ? 'alkanes-thesis-composer' : 'alkanes-social-composer'
	const inputTestId = context ? 'alkanes-thesis-input' : 'alkanes-twetch-composer-input'
	const postTestId = context ? 'alkanes-thesis-post' : 'alkanes-twetch-post'
	const submitPost = () => {
		if (!loggedIn) {
			event.emit('command', { id: 'connect-wallet' })
			return
		}
		composer.submit(
			context ? withTickerPrefix(context.symbol, text.trim()) : text.trim(),
			effectiveTags,
			replyTo ? { postId: replyTo.postId, txid: replyTo.txid } : null,
			turnstileToken,
			// A token's thesis box publishes as Twetch activity about that token.
			context
				? {
						namespace: context.namespace ?? 'alkanes',
						id: context.id,
						symbol: context.symbol.toUpperCase()
				  }
				: null
		)
	}

	return (
		<div
			data-testid={composerTestId}
			className={cn(
				'transition-shadow duration-300',
				!bare && 'rounded-2xl bg-card',
				posted && !bare && 'ring-1 ring-success/50',
				bare ? '' : compact ? 'p-4' : 'mx-4 mt-4 px-5 py-4'
			)}
		>
			{loggedIn && !watchMode && !identity && !turnstileFailed && (
				<TwetchTurnstile
					appearance="interaction-only"
					resetKey={turnstileResetKey}
					onToken={setTurnstileToken}
					onError={() => {
						setTurnstileFailed(true)
						setTurnstileToken(null)
					}}
				/>
			)}

			<div className={cn('flex items-start', compact ? 'gap-2.5' : 'gap-3')}>
				<ViewerFace connected={loggedIn && !watchMode} small={compact} />
				<div className="min-w-0 flex-1">
					{!compact && (
						<div className="mb-1.5 flex items-center gap-2">
							<span
								data-testid="alkanes-twetch-identity-chip"
								className="min-w-0 flex-1 truncate text-sm font-semibold"
							>
								{viewer.name && loggedIn && !watchMode ? `${viewer.name} · ` : ''}
								<span className={viewer.name && loggedIn && !watchMode ? 'font-normal text-muted-foreground' : undefined}>
									{identity ? `Twetch #${identity.uid}` : 'Post with wallet'}
								</span>
							</span>
							{identity && loggedIn && !watchMode && (
								<button
									type="button"
									data-testid="alkanes-twetch-signout"
									onClick={signOut}
									className="alkanes-touch inline-flex shrink-0 items-center gap-1 text-xxs text-muted-foreground transition-colors hover:text-foreground"
								>
									<LogOut className="size-3" aria-hidden="true" />
									Disconnect Twetch
								</button>
							)}
						</div>
					)}
			<label htmlFor={inputId} className="sr-only">
				{replyTo
					? `Reply to ${replyTo.author}`
					: context
					? `Write a thesis on ${context.label ?? context.symbol}`
					: marketPicker
					? 'Write a thesis'
					: 'Write a post'}
			</label>
			<textarea
				id={inputId}
				ref={inputRef}
				data-testid={inputTestId}
				value={text}
				onChange={(e) => changeText(e.target.value)}
				role="combobox"
				aria-autocomplete="list"
				aria-haspopup="listbox"
				aria-expanded={Boolean(activeCashtag && !tokenPickerDismissed)}
				aria-controls={activeCashtag && !tokenPickerDismissed ? listboxId : undefined}
				aria-activedescendant={
					activeCashtag && !tokenPickerDismissed && tokenOptions[tokenActive]
						? `${listboxId}-${tokenActive}`
						: undefined
				}
				onKeyDown={(keyboardEvent) => {
					// The universal composer chord, checked before the picker so a
					// deliberate send wins even mid-cashtag.
					if (keyboardEvent.key === 'Enter' && (keyboardEvent.metaKey || keyboardEvent.ctrlKey)) {
						keyboardEvent.preventDefault()
						if (canPost) {
							submitPost()
						}
						return
					}
					if (!activeCashtag || tokenPickerDismissed) return
					if (keyboardEvent.key === 'ArrowDown' && tokenOptions.length) {
						keyboardEvent.preventDefault()
						setTokenActive((index) => (index + 1) % tokenOptions.length)
					} else if (keyboardEvent.key === 'ArrowUp' && tokenOptions.length) {
						keyboardEvent.preventDefault()
						setTokenActive((index) => (index - 1 + tokenOptions.length) % tokenOptions.length)
					} else if (
						(keyboardEvent.key === 'Enter' || keyboardEvent.key === 'Tab') &&
						tokenOptions[tokenActive]
					) {
						keyboardEvent.preventDefault()
						selectToken(tokenOptions[tokenActive])
					} else if (keyboardEvent.key === 'Escape') {
						keyboardEvent.preventDefault()
						setTokenPickerDismissed(true)
					}
				}}
				disabled={busy || watchMode || (loggedIn && (composer.eligible === false || !capable))}
				rows={compact ? 2 : 3}
				placeholder={
					watchMode
						? 'Exit watch mode to post'
						: loggedIn && !capable
						? 'Connect a bc1q or bc1p wallet that supports message signing'
						: loggedIn && composer.eligible === false
						? 'Posting is paused'
						: replyTo
						? `Reply to ${replyTo.author}…`
						: context
						? `Write a thesis on ${context.label ?? context.symbol}…`
						: marketPicker
						? 'Write a thesis'
						: "What's moving?"
				}
				className={cn(
					'w-full resize-none rounded-xl bg-secondary/40 px-3 py-2.5 text-base leading-snug outline-none ring-1 ring-inset ring-border/60 transition-shadow placeholder:text-muted-foreground sm:text-sm focus:ring-primary/70 disabled:cursor-not-allowed disabled:opacity-70',
				)}
			/>
			{activeCashtag && !tokenPickerDismissed && (
				<div
					id={listboxId}
					className="mt-1 overflow-hidden rounded-xl border border-border/70 bg-background shadow-lg"
					role="listbox"
					aria-label="Markets"
				>
					<div className="px-3 py-1.5 text-xxs font-semibold text-muted-foreground">
						Markets
					</div>
					{tokenOptions.map((option, index) => (
						<button
							key={option.key}
							id={`${listboxId}-${index}`}
							type="button"
							role="option"
							aria-selected={index === tokenActive}
							onMouseDown={(mouseEvent) => mouseEvent.preventDefault()}
							onClick={() => selectToken(option)}
							className={cn(
								'flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-secondary/60',
								index === tokenActive && 'bg-secondary/60'
							)}
						>
							{option.icon ? (
								 
								<img
									src={option.icon}
									alt=""
									loading="lazy"
									className="size-7 shrink-0 rounded-full object-cover [image-rendering:pixelated]"
								/>
							) : (
								<span className="grid size-7 shrink-0 place-items-center rounded-full bg-secondary text-xs">
									$
								</span>
							)}
							<span className="min-w-0 flex-1">
								<span className="block truncate text-sm font-semibold">{option.name}</span>
								<span className="block truncate text-xxs text-muted-foreground">
									${option.symbol} · {option.detail}
								</span>
							</span>
						</button>
					))}
					{tokenOptions.length === 0 && (
						<span className="block px-3 py-2.5 text-xs text-muted-foreground">
							{tokenLoading ? 'Searching markets…' : 'No market matches that'}
						</span>
					)}
				</div>
			)}

			{/* Every token the post will link, visible before it's signed —
			    tagging is a commitment, so nothing rides along silently. The
			    asset the thesis is ON stays quiet: its chip would repeat what
			    the composer heading already says. */}
			{effectiveTags.filter((tag) => tag.id !== context?.id).length > 0 && (
				<div className="mt-2 flex flex-wrap gap-1.5" data-testid="alkanes-composer-tags">
					{effectiveTags
						.filter((tag) => tag.id !== context?.id)
						.map((tag) => {
							const asset = assets.find(
								(a) => a.id === tag.id && (tag.namespace ?? 'alkanes') === 'alkanes'
							)
							return (
								<span
									key={tag.id}
									className="inline-flex items-center gap-1 rounded-full bg-primary/10 py-0.5 pl-1 pr-2 text-xxs font-semibold text-primary motion-safe:animate-[alkanes-chip-in_0.25s_cubic-bezier(0.22,1,0.36,1)]"
								>
									{asset?.icon ? (
										 
										<img
											src={asset.icon}
											alt=""
											width={14}
											height={14}
											className="size-3.5 rounded-full object-cover"
										/>
									) : (
										<span className="grid size-3.5 place-items-center rounded-full bg-primary/20 text-[9px]">
											$
										</span>
									)}
									${tag.symbol}
								</span>
							)
						})}
				</div>
			)}

			{/* The refusal, on its own line and in full — squeezed into the action
			    row beside the market picker it truncated to a single letter. */}
			{failed && (
				<p
					role="alert"
					data-testid="alkanes-composer-error"
					className="mt-2 text-xs leading-relaxed text-destructive-ink"
				>
					{composer.error?.message}
				</p>
			)}

			{/* One line, always: the market chip gives up width (and ellipsizes a
			    long name) so the action never drops to a row of its own. */}
			<div className="mt-2 flex items-center gap-2">
				<span className="sr-only" role="status" aria-live="polite">
					{posted ? 'Post published.' : composer.error?.message ?? ''}
				</span>
				{marketPicker}
				<button
					type="button"
					data-testid={postTestId}
					onClick={submitPost}
					disabled={busy || posted || unavailable || (loggedIn && !canPost)}
					aria-label={composer.error?.message ?? actionLabel}
					className={cn(
						'alkanes-touch ml-auto inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl font-semibold transition-colors',
						compact ? 'h-9 px-3.5 text-xs' : 'h-10 px-4 text-sm',
						posted
							? 'cursor-default bg-success text-success-foreground'
							: failed
							? canPost
								? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
								: 'cursor-not-allowed bg-destructive/70 text-destructive-foreground'
							: !loggedIn || canPost
							? 'bg-primary text-primary-foreground hover:bg-primary/90'
							: 'cursor-not-allowed bg-secondary text-muted-foreground'
					)}
				>
					{posted ? (
						<Check
							className="size-3.5 motion-safe:animate-[alkanes-pop_0.35s_cubic-bezier(0.22,1,0.36,1)]"
							aria-hidden="true"
						/>
					) : failed ? (
						<CircleAlert className="size-3.5" aria-hidden="true" />
					) : (
						<PenLine className="size-3.5" aria-hidden="true" />
					)}
					<span
						{...(posted
							? { 'data-testid': 'alkanes-twetch-posted' }
							: composer.error
							? { 'data-testid': 'alkanes-twetch-post-error' }
							: {})}
					>
						{actionLabel}
					</span>
				</button>
			</div>
				</div>
			</div>
		</div>
	)
}

/// The viewer's own face beside the box, as on Twetch — and the door to their
/// profile: one tap opens the shared editor (create it, rename, pick a
/// picture) from wherever they happen to be writing.
function ViewerFace({ connected, small = false }: { connected: boolean; small?: boolean }) {
	const viewer = useViewerProfile()
	const [broken, setBroken] = React.useState<string | null>(null)
	const src = resolveTwetchImage(viewer.icon)
	// Not filled out: no Twetch account at all, or one still missing a name or
	// a picture. These are the people the picture is a door for, and the only
	// ones who see it marked as one — it stops the moment they finish.
	const unfinished = viewer.userId === null || !viewer.name?.trim() || !viewer.icon
	const label = viewer.userId !== null ? 'Edit your profile' : 'Set up your profile'
	const face =
		src && src !== broken ? (
			<img
				src={src}
				alt=""
				onError={() => setBroken(src)}
				className="size-full object-cover [image-rendering:pixelated]"
			/>
		) : viewer.name ? (
			<span className="text-xs font-bold">{viewer.name.slice(0, 2).toUpperCase()}</span>
		) : (
			<User className="size-4" aria-hidden="true" />
		)
	const shell =
		`grid ${small ? 'size-8' : 'size-9'} shrink-0 place-items-center overflow-hidden rounded-full bg-secondary text-muted-foreground`
	if (!connected) {
		return (
			<span aria-hidden="true" className={shell}>
				{face}
			</span>
		)
	}
	return (
		// The BUTTON is the whole mark, badge included: the plus overhangs the
		// picture, and a plus you cannot press is a worse invitation than none.
		// The circle keeps its own clipping on the inner span so the overhang
		// survives.
		<button
			type="button"
			data-testid="alkanes-composer-face"
			onClick={openProfileEditor}
			aria-label={label}
			title={label}
			className={cn(
				`relative ${small ? 'size-8' : 'size-9'} shrink-0 rounded-full`,
				'transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
				// The affordance used to arrive only on hover, which is no
				// affordance at all on a phone. An unfinished profile wears a
				// DOTTED outline from the start — the drawn-in placeholder every
				// upload control uses, which says "not filled yet" where a solid
				// ring would just say "selected". A finished one still lights up
				// under the pointer. Outline, not ring: a ring is a box-shadow
				// and cannot be dotted.
				unfinished
					? 'outline outline-2 outline-dotted outline-offset-2 outline-primary/70'
					: 'ring-2 ring-transparent hover:ring-primary/60'
			)}
		>
			<span className={cn(shell, 'size-full')}>{face}</span>
			{unfinished && (
				// A plus, so the mark says what the tap DOES rather than only that
				// something is unread. Ringed in the card colour so it reads as a
				// badge sitting on the picture, not part of it.
				<span
					aria-hidden="true"
					data-testid="alkanes-composer-face-badge"
					className="absolute -bottom-1 -right-1 grid size-4 place-items-center rounded-full bg-primary text-primary-foreground ring-2 ring-card"
				>
					<Plus className="size-3" strokeWidth={3} />
				</span>
			)}
		</button>
	)
}
